"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommonQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    index: "Q.01",
    question: "How do I determine which training program fits my goals?",
    answer: "We conduct a comprehensive physical assessment during your initial consultation to analyze your current baseline, movement patterns, and recovery capacity. Whether your objective is explosive power, fat loss, or functional longevity, we engineer a program that aligns with your specific physiological requirements and schedule.",
    footnote: "Related — View our training methodology"
  }, {
    index: "Q.02",
    question: "What is the intensity level of your group sessions?",
    answer: "Our sessions are designed for high-output performance. We utilize interval-based training, compound lifting, and metabolic conditioning to maximize caloric burn and muscle recruitment. Every movement is tracked, and intensity is scaled to ensure you are operating at your peak capacity without compromising form.",
    footnote: "Related — Performance tracking standards"
  }, {
    index: "Q.03",
    question: "Do you provide nutritional guidance with coaching?",
    answer: "Nutrition is the fuel for your performance. We provide macro-nutrient targets and hydration protocols tailored to your training volume. Our approach focuses on sustainable, high-performance fueling rather than restrictive dieting, ensuring you have the energy to crush every workout.",
    footnote: "Related — Performance nutrition guide"
  }, {
    index: "Q.04",
    question: "Can I train with you if I have previous injuries?",
    answer: "Absolutely. We specialize in corrective exercise and injury mitigation. Our trainers work to identify imbalances and build a foundation of stability and mobility before progressing to high-intensity loads. We prioritize longevity so you can train harder for longer.",
    footnote: "Related — Injury prevention protocols"
  }, {
    index: "Q.05",
    question: "How often should I train to see results?",
    answer: "Consistency is the primary driver of adaptation. For most clients, we recommend a minimum of three high-intensity sessions per week to see measurable changes in body composition and strength. We will build a schedule that fits your lifestyle while ensuring you hit the necessary volume for progress.",
    footnote: "Related — Training frequency guide"
  }, {
    index: "Q.06",
    question: "What qualifications do your trainers hold?",
    answer: "Every trainer at Peak Force Fitness is certified through elite industry organizations and undergoes rigorous internal training in biomechanics, program design, and performance psychology. We are not just instructors; we are performance engineers dedicated to your success.",
    footnote: "Related — Meet the coaching staff"
  }, {
    index: "Q.07",
    question: "Do you offer remote or online coaching options?",
    answer: "Yes. Our digital coaching platform delivers your custom program directly to your device, complete with video tutorials, progress tracking, and direct communication with your coach. You get the same engineering precision as our in-person clients, anywhere in the world.",
    footnote: "Related — Online coaching details"
  }, {
    index: "Q.08",
    question: "What happens if I miss a scheduled session?",
    answer: "We operate on a performance-first schedule. If you need to reschedule, we require 24 hours' notice to adjust your program and ensure your training volume remains consistent. We are committed to your progress and expect the same level of dedication in return.",
    footnote: "Related — Scheduling and policy"
  }];
  return <section data-section-id="1435" id="common-questions" className="relative bg-background py-28 md:py-40">
      <div className="mx-auto max-w-[1180px] px-5 md:px-8 lg:px-12">
        <div ref={ref} className={`grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="md:col-span-4">
            <div className="md:sticky md:top-28 relative">
              {}

              <div className="relative">
                {}
                

                {}
                

                {}
                <h2 className="font-default mt-8 font-sans font-bold text-[clamp(2.5rem,5.5vw,5rem)] leading-[0.96] tracking-tighter text-foreground">
                  The questions that{" "}
                  <em className="italic text-primary not-italic-fallback">
                    drive
                  </em>{" "}
                  results.
                </h2>

                {}
                <p className="mt-8 max-w-[220px] text-[1.0625rem] md:text-[1.1875rem] leading-[1.55] text-foreground/80">
                  Clear answers for those ready to engineer their peak physical potential.
                </p>

                {}
                <Link to="/JoinTheForcePage" className="group mt-10 inline-flex items-center gap-2 font-mono text-[0.8125rem] uppercase tracking-[0.14em] text-foreground hover:text-primary transition-colors duration-240">
                  <span>Contact a coach</span>
                  <ArrowRight className="size-4 text-primary transition-transform duration-240 ease-out group-hover:translate-x-1.5" strokeWidth={1.25} />
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={faq.index} value={`item-${i}`} data-index={i} className="border-b-0 border-t border-border last:border-b last:border-border">
                  <AccordionTrigger className="group py-7 md:py-7 hover:no-underline rounded-md gap-6 items-center [&>svg]:hidden">
                    <div className="flex flex-1 items-center gap-6 md:gap-10 text-left">
                      <span className="font-mono text-[0.75rem] md:text-[0.8125rem] uppercase tracking-[0.14em] text-primary shrink-0 pt-1">
                        {faq.index}
                      </span>
                      <span className="font-default text-[1.05rem] md:text-[1.25rem] font-bold leading-[1.3] text-foreground tracking-tight">
                        {faq.question}
                      </span>
                    </div>
                    <span aria-hidden="true" className="relative inline-flex size-6 shrink-0 items-center justify-center text-primary transition-transform duration-[260ms] ease-[cubic-bezier(0.65,0,0.35,1)] group-data-[state=open]:rotate-45">
                      <span className="absolute h-px w-6 bg-current" />
                      <span className="absolute h-6 w-px bg-current" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-2">
                    <div className="pl-0 md:pl-[5.5rem] max-w-[720px]">
                      <p className="text-[0.9375rem] md:text-[1rem] leading-[1.6] text-foreground/70">
                        {faq.answer}
                      </p>

                      <div className="mt-6 flex items-center gap-1.5">
                        <span className="h-px w-20 bg-primary" />
                        <span className="size-1.5 rounded-lg bg-primary" />
                      </div>

                      <p className="mt-4 font-mono text-[0.75rem] uppercase tracking-[0.14em] text-primary/70">
                        {faq.footnote}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}